import React, { useState, useEffect, useMemo } from "react";
import { ScrollView, View, StyleSheet, TextInput } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { Menu } from "@/components/Menu";
import { listService, IncidentType } from "@/services/listService";
import useAuth from "@/hooks/useAuth";
import { useLocalSearchParams, router } from "expo-router";
import { getEntityPermissions } from "@/lib/permissions";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import { useSettingsSearch } from "@/contexts/SettingsSearchContext";

export default function IncidentTypesPage() {
  const { colors } = useTheme();
  const { tenantId, userRoles } = useAuth();
  const permissions = getEntityPermissions(userRoles, "incident_types");
  const params = useLocalSearchParams<{ create?: string }>();

  const [incidentTypes, setIncidentTypes] = useState<IncidentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { searchValue } = useSettingsSearch("incident_types");

  const filteredIncidentTypes = useMemo(() => {
    if (!searchValue.trim()) return incidentTypes;
    const search = searchValue.toLowerCase();
    return incidentTypes.filter(
      (item) => item.name?.toLowerCase().includes(search) || item.description?.toLowerCase().includes(search),
    );
  }, [incidentTypes, searchValue]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<{ name: string; description: string }>({
    name: "",
    description: "",
  });

  // Load incident types
  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listService.getIncidentTypes();
      setIncidentTypes(data);
    } catch (err: any) {
      setError(err.message || "Failed to load incident types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Watch for create param changes
  useEffect(() => {
    if (params.create === "true") {
      setShowForm(true);
      setEditingId(null);
      setFormData({ name: "", description: "" });
    }
  }, [params.create]);

  // Menu actions
  const handleEdit = (type: IncidentType) => {
    setEditingId(type.id);
    setFormData({
      name: type.name,
      description: type.description || "",
    });
    setShowForm(true);
    setError(null);
  };

  const handleDuplicate = (type: IncidentType) => {
    setEditingId(null); // No ID means it's a new item
    setFormData({
      name: type.name,
      description: type.description || "",
    });
    setShowForm(true);
    setError(null);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this incident type?")) return;

    try {
      await listService.deleteIncidentType(id);
      setIncidentTypes(incidentTypes.filter((type) => type.id !== id));
    } catch (err: any) {
      setError(err.message || "Failed to delete incident type");
    }
  };

  // Form submit handler (handles both add and update)
  const handleSubmit = async () => {
    if (!tenantId || !formData.name.trim()) {
      setError("Name is required");
      return;
    }

    try {
      if (editingId) {
        // Update existing incident type
        const updated = await listService.updateIncidentType(editingId, {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
        });
        setIncidentTypes(incidentTypes.map((type) => (type.id === editingId ? updated : type)));
      } else {
        // Add new incident type
        const type = await listService.addIncidentType({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          tenant_id: tenantId,
          meta_data: {},
        });
        setIncidentTypes([...incidentTypes, type]);
      }

      // Reset form
      setFormData({ name: "", description: "" });
      setShowForm(false);
      setEditingId(null);
      setError(null);
      router.setParams({ create: undefined });
    } catch (err: any) {
      setError(err.message || (editingId ? "Failed to update incident type" : "Failed to add incident type"));
    }
  };

  const handleCancel = () => {
    setFormData({ name: "", description: "" });
    setShowForm(false);
    setEditingId(null);
    setError(null);
    router.setParams({ create: undefined });
  };

  if (!permissions.read) {
    return <NoPermissionScreen entityName="incident types" />;
  }

  return (
    <ScrollView style={styles.container}>
      <View>
        {loading ? (
          <ThemedText style={styles.centered}>Loading incident types...</ThemedText>
        ) : error ? (
          <View style={styles.errorContainer}>
            <ThemedText type="defaultSemiBold" style={{ color: colors.red.text }}>
              {error}
            </ThemedText>
            <Button text="Retry" icon="refresh" style={{ marginTop: 8 }} onPress={load} />
          </View>
        ) : (
          <View style={styles.cardsContainer}>
            {permissions.create && showForm && !editingId && (
              <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={styles.editForm}>
                  <TextInput
                    style={[
                      styles.input,
                      { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                    ]}
                    placeholder="Name *"
                    placeholderTextColor={colors.placeholder}
                    value={formData.name}
                    onChangeText={(text) => setFormData({ ...formData, name: text })}
                    autoFocus
                  />
                  <TextInput
                    style={[
                      styles.input,
                      styles.descriptionInput,
                      { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                    ]}
                    placeholder="Description (optional)"
                    placeholderTextColor={colors.placeholder}
                    value={formData.description}
                    onChangeText={(text) => setFormData({ ...formData, description: text })}
                    multiline
                  />
                  <View style={styles.editActions}>
                    <Button
                      text="Cancel"
                      style={[styles.smallBtn, { borderColor: colors.border }]}
                      textStyle={{ fontSize: 13 }}
                      onPress={handleCancel}
                    />
                    <Button
                      text="Add"
                      icon="plus"
                      iconPosition="right"
                      style={[styles.smallBtn, { backgroundColor: colors.primary }]}
                      textStyle={{ fontSize: 13, color: "white" }}
                      onPress={handleSubmit}
                      enabled={!!formData.name.trim()}
                    />
                  </View>
                </View>
              </View>
            )}

            {filteredIncidentTypes.map((type) => {
              const isEditing = editingId === type.id;

              return (
                <View key={type.id} style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
                  {isEditing ? (
                    <View style={styles.editForm}>
                      <TextInput
                        style={[
                          styles.input,
                          {
                            borderColor: colors.border,
                            color: colors.text,
                            backgroundColor: colors.backgroundSecondary,
                          },
                        ]}
                        placeholder="Name *"
                        placeholderTextColor={colors.placeholder}
                        value={formData.name}
                        onChangeText={(text) => setFormData({ ...formData, name: text })}
                        autoFocus
                      />
                      <TextInput
                        style={[
                          styles.input,
                          styles.descriptionInput,
                          {
                            borderColor: colors.border,
                            color: colors.text,
                            backgroundColor: colors.backgroundSecondary,
                          },
                        ]}
                        placeholder="Description (optional)"
                        placeholderTextColor={colors.placeholder}
                        value={formData.description}
                        onChangeText={(text) => setFormData({ ...formData, description: text })}
                        multiline
                      />
                      <View style={styles.editActions}>
                        <Button
                          text="Cancel"
                          style={[styles.smallBtn, { borderColor: colors.border }]}
                          textStyle={{ fontSize: 13 }}
                          onPress={handleCancel}
                        />
                        <Button
                          text="Update"
                          icon="check"
                          iconPosition="right"
                          style={[styles.smallBtn, { backgroundColor: colors.primary }]}
                          textStyle={{ fontSize: 13, color: "white" }}
                          onPress={handleSubmit}
                          enabled={!!formData.name.trim()}
                        />
                      </View>
                    </View>
                  ) : (
                    <View style={styles.itemContent}>
                      <View style={styles.itemInfo}>
                        <View style={styles.itemHeader}>
                          <ThemedText type="defaultSemiBold">{type.name}</ThemedText>
                          <Menu
                            buttonStyle={{
                              marginLeft: "auto",
                              alignSelf: "flex-start",
                              borderWidth: 0,
                              backgroundColor: "transparent",
                            }}
                            hideArchive={true}
                            canEdit={permissions.update}
                            canDuplicate={permissions.create}
                            canDelete={permissions.delete}
                            onEdit={() => handleEdit(type)}
                            onDuplicate={() => handleDuplicate(type)}
                            onDelete={() => handleDelete(type.id)}
                          />
                        </View>
                        {type.description && (
                          <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
                            {type.description}
                          </ThemedText>
                        )}
                      </View>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: 16,
  },
  cardsContainer: {
    gap: 12,
  },
  card: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    overflow: "hidden",
  },
  itemContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
  },
  itemInfo: {
    flex: 1,
    gap: 4,
  },
  itemHeader: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 8,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  description: {
    fontSize: 13,
  },
  editForm: {
    padding: 16,
    gap: 12,
  },
  input: {
    height: 40,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  descriptionInput: {
    height: 60,
    paddingTop: 10,
    textAlignVertical: "top",
  },
  editActions: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
  },
  smallBtn: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  centered: {
    textAlign: "center",
    paddingVertical: 32,
    opacity: 0.7,
  },
  errorContainer: {
    alignItems: "center",
    paddingVertical: 32,
  },
});
